import { API_BASE_URL as BASE } from './apiConfig';
import { createAuthedAxios } from '@/lib/authedAxios';

const client = createAuthedAxios({ baseURL: `${BASE}/api` });

client.interceptors.response.use(
  (res) => {
    if (res.data && typeof res.data === 'object' && 'success' in res.data && 'data' in res.data) {
      res.data = res.data.data;
    }
    return res;
  },
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── 타입 ─────────────────────────────────────────────────────

export type InquiryStatus = 'PENDING' | 'ANSWERED' | 'CLOSED';

export interface InquiryListItem {
  id: number;
  title: string;
  status: InquiryStatus;
  isSecret: boolean;
  hasAnswer: boolean;
  isReadByUser: boolean;
  createdAt: string;
}

export interface InquiryListResponse {
  content: InquiryListItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  // unreadAnswerCount는 별도 GET /api/inquiries/unread-count API로 분리됨 (B/E 구현 기준)
}

export interface InquiryAnswer {
  id: number;
  content: string;
  adminName: string;
  createdAt: string;
  updatedAt?: string;
}

export interface InquiryDetail {
  id: number;
  title: string;
  content: string;
  status: InquiryStatus;
  isSecret: boolean;
  createdAt: string;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  answer: InquiryAnswer | null;
}

export interface InquiryCreateRequest {
  title: string;
  content: string;
  isSecret: boolean;
}

// 관리자용
export interface AdminInquiryListItem {
  id: number;
  userId: string;
  userEmail: string;
  userName: string;
  title: string;
  status: InquiryStatus;
  isSecret: boolean;
  createdAt: string;
}

export interface AdminInquiryListResponse {
  content: AdminInquiryListItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  pendingCount: number;
}

export interface AdminInquiryDetail extends InquiryDetail {
  userId: string;
  userEmail: string;
  userName: string;
}

// ── 사용자 API ───────────────────────────────────────────────

export const inquiryApi = {
  getMyInquiries: (page: number, size: number) =>
    client.get<InquiryListResponse>('/inquiries/me', { params: { page, size } }),

  getDetail: (id: number) =>
    client.get<InquiryDetail>(`/inquiries/${id}`),

  create: (data: { title: string; content: string; isSecret: boolean; file?: File }) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('content', data.content);
    formData.append('isSecret', String(data.isSecret));
    if (data.file) formData.append('file', data.file);
    return client.post<{ id: number; title: string; status: InquiryStatus; createdAt: string }>(
      '/inquiries',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  update: (id: number, data: { title: string; content: string }) =>
    client.put(`/inquiries/${id}`, data),

  delete: (id: number) =>
    client.delete(`/inquiries/${id}`),

  getUnreadCount: () =>
    client.get<{ count: number }>('/inquiries/unread-count'),
};

// ── 관리자 API ───────────────────────────────────────────────

export const adminInquiryApi = {
  getList: (page: number, size: number, status?: InquiryStatus) =>
    client.get<AdminInquiryListResponse>('/admin/inquiries', { params: { page, size, ...(status ? { status } : {}) } }),

  getDetail: (id: number) =>
    client.get<AdminInquiryDetail>(`/admin/inquiries/${id}`),

  createAnswer: (id: number, data: { content: string }) =>
    client.post(`/admin/inquiries/${id}/answer`, data),

  updateAnswer: (id: number, data: { content: string }) =>
    client.put(`/admin/inquiries/${id}/answer`, data),

  updateStatus: (id: number, status: InquiryStatus) =>
    client.patch(`/admin/inquiries/${id}/status`, { status }),
};
